import { APIAccessor, HttpError, ListResponse } from '../utils/api';
import { BaseModel } from './base';

export class UserModel extends BaseModel {
    public id = '';
    public username = '';

    // eslint-disable-next-line unicorn/consistent-class-member-order -- conflicts with @typescript-eslint/member-ordering, which requires instance fields before static methods
    public static async getById(id: string, apiAccessor: APIAccessor): Promise<UserModel | undefined> {
        try {
            const api = await apiAccessor.fetch(`/api/v1/users/${encodeURIComponent(id)}`);
            return this.wrapNew(api);
        } catch (error) {
            if (error instanceof HttpError && (error.status === 404 || error.status === 403)) {
                return undefined;
            }

            throw error;
        }
    }

    public static wrapNew(object: unknown): UserModel {
        const user = new UserModel();
        return user.wrap(object);
    }
}

export class UserDetailsModel extends UserModel {
    public email = '';
    public api_key = '';
    public storage_quota = 0;
    public storage_used = 0;
    public active = false;
    public email_valid = false;
    public approved = false;
    public totp_enabled = false;
    public admin = false;
    public security_version = 0;

    // eslint-disable-next-line unicorn/consistent-class-member-order -- conflicts with @typescript-eslint/member-ordering, which requires instance fields before static methods
    public static async getById(id: string, apiAccessor: APIAccessor): Promise<UserDetailsModel | undefined> {
        try {
            const api = await apiAccessor.fetch(`/api/v1/users/${encodeURIComponent(id)}/details`);
            return this.wrapNew(api);
        } catch (error) {
            if (error instanceof HttpError && (error.status === 404 || error.status === 403)) {
                return undefined;
            }

            throw error;
        }
    }

    public static wrapNew(object: unknown): UserDetailsModel {
        const userDetails = new UserDetailsModel();
        return userDetails.wrap(object);
    }
}

export type AdminUserStatus = 'all' | 'approved' | 'pending' | 'unverified';

export interface AdminUserListOptions {
    status: AdminUserStatus;
    search: string;
    offset: number;
    limit: number;
}

export interface AdminUserList {
    items: AdminUserModel[];
    total: number;
}

export class AdminUserModel extends UserModel {
    public email = '';
    public storage_quota = 0;
    public storage_used = 0;
    public active = false;
    public email_valid = false;
    public approved = false;
    public totp_enabled = false;
    public admin = false;

    // eslint-disable-next-line unicorn/consistent-class-member-order -- conflicts with @typescript-eslint/member-ordering, which requires instance fields before static methods
    public static async list(options: AdminUserListOptions, apiAccessor: APIAccessor): Promise<AdminUserList> {
        const query = new URLSearchParams({
            status: options.status,
            search: options.search,
            offset: options.offset.toString(),
            limit: options.limit.toString(),
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const response = (await apiAccessor.fetch(`/api/v1/users?${query.toString()}`)) as ListResponse;
        return {
            items: response.items.map((item) => this.wrapNew(item)),
            total: response.total,
        };
    }

    public static wrapNew(object: unknown): AdminUserModel {
        const user = new AdminUserModel();
        return user.wrap(object);
    }

    public async approve(apiAccessor: APIAccessor): Promise<AdminUserModel> {
        const api = await apiAccessor.fetch(`/api/v1/users/${encodeURIComponent(this.id)}/approve`, {
            method: 'POST',
        });
        return AdminUserModel.wrapNew(api);
    }

    public async revokeApproval(apiAccessor: APIAccessor): Promise<AdminUserModel> {
        const api = await apiAccessor.fetch(`/api/v1/users/${encodeURIComponent(this.id)}/approve`, {
            method: 'DELETE',
        });
        return AdminUserModel.wrapNew(api);
    }
}
